import React, { useState, useEffect } from "react";
import Modal from "../Modal/Modal";
import TransporterService from "../../services/TransporterService";
import OnboardingService from "../../services/OnboardingService";
import { useLoader } from "../../contexts/LoaderContext";
import { useAuth } from "../../contexts/AuthContext";
import { showToast } from "../Toaster";
import { IOnboardingFormData } from "../../interfaces/onboarding.interface";
import { generateIdentifier } from "../../utils/identifierGenerator";
import rbacUtils from "../../utils/rbacUtils";

interface TransporterRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TransporterRegistrationModal: React.FC<
  TransporterRegistrationModalProps
> = ({ isOpen, onClose, onSuccess }) => {
  const { showLoader, hideLoader } = useLoader();
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState<IOnboardingFormData>({
    name: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    identifier: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Clear any previous errors when modal opens
      setErrors({});

      // Always generate a unique identifier when modal opens
      if (formData.name) {
        const identifier = generateIdentifier(formData.name, 6);
        setFormData((prev) => ({
          ...prev,
          identifier: identifier,
        }));
      }
    }
  }, [formData.name, isOpen]);

  // Generate identifier whenever name changes
  useEffect(() => {
    if (formData.name.trim()) {
      const identifier = generateIdentifier(formData.name, 6);
      setFormData((prev) => ({
        ...prev,
        identifier: identifier,
      }));
    }
  }, [formData.name]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Transporter name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\s/g, ""))) {
      newErrors.phoneNumber = "Please enter a valid phone number (10 digits)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Ensure identifier is always generated before submission
    if (!formData.identifier && formData.name) {
      const identifier = generateIdentifier(formData.name, 6);
      setFormData((prev) => ({
        ...prev,
        identifier: identifier,
      }));
    }

    // Create submission data with username set to name
    const submissionData = {
      ...formData,
      username: formData.name,
    };

    setIsSubmitting(true);
    showLoader();

    try {
      let response;

      // Determine which service method to use based on user role
      if (userProfile && rbacUtils.isEnterpriseAdmin(userProfile)) {
        // Enterprise admin creates transporter under enterprise
        response = await OnboardingService.createTransporterAdmin(
          submissionData
        );
      } else if (userProfile && rbacUtils.isFederationAdmin(userProfile)) {
        // Federation admin creates transporter under federation
        response = await OnboardingService.createFederationTransporterAdmin(
          submissionData
        );
      } else {
        // Fallback to enterprise endpoint for backward compatibility
        response = await TransporterService.createTransporterAdmin(
          submissionData
        );
      }

      console.log(response);

      if (response.error) {
        showToast(response.message, "error");
      } else {
        showToast("Transporter created successfully", "success");
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error("Error creating transporter:", error);
      // Handle other errors
      showToast(
        error.response?.data?.message || "Error creating transporter",
        "error"
      );
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  const getFooterButtons = () => (
    <div className="flex justify-center gap-4">
      <button
        type="button"
        className="secondary_button w-[128px]"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="primary_button w-[128px]"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Creating..." : "Create"}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Transporter"
      size="xl"
      closeOnModalOverlayClick={true}
      footer={getFooterButtons()}
    >
      <div className="modal-form">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Transporter Name */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Transporter Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter transporter name"
              className={`form-input ${errors.name ? "error" : ""}`}
            />
            {errors.name && (
              <div className="form-help error">{errors.name}</div>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label required_field_label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              className={`form-input ${errors.email ? "error" : ""}`}
            />
            {errors.email && (
              <div className="form-help error">{errors.email}</div>
            )}
          </div>

          {/* Phone Number */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Phone Number
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              className={`form-input ${errors.phoneNumber ? "error" : ""}`}
            />
            {errors.phoneNumber && (
              <div className="form-help error">{errors.phoneNumber}</div>
            )}
          </div>

          {/* Password */}
          <div className="form-group">
            <label className="form-label required_field_label">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter password"
              className={`form-input ${errors.password ? "error" : ""}`}
            />
            {errors.password && (
              <div className="form-help error">{errors.password}</div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label className="form-label required_field_label">
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm password"
              className={`form-input ${errors.confirmPassword ? "error" : ""}`}
            />
            {errors.confirmPassword && (
              <div className="form-help error">{errors.confirmPassword}</div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TransporterRegistrationModal;
